import type { FieldType } from '@kizen/api';
import { Dispatch, PropsWithChildren, SetStateAction } from 'react';
import type { Summary, UseGetSummaryOptions } from './model';
import type { MergeFields } from 'components/WYSIWYG/types';

import { format } from 'date-fns/format';
import isNumber from 'lodash/isNumber';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { KizenTypography, fontWeights } from '__app/typography';
import Switch from '__components/Kizen/Switch';
import TextTemplater from 'components/Kizen/TextTemplater';
import KizenLoader from 'components/Kizen/Loader';
import { useSummaryCounts } from 'hooks/useSummaryCounts';
import { parseDateFromApi } from 'services/helpers';
import { FIELD_TYPES } from 'utility/constants';
import { subscriptionStatusLabel } from '../../ContactDetail/pages/Messages/columns';
import {
  SEND_EMAIL_IDENTIFIER,
  SEND_TEXT_IDENTIFIER,
  CHANGE_TAGS_IDENTIFIER,
  MANAGE_TEAM_IDENTIFIER,
  MANAGE_SUBSCRIPTIONS,
  EXPORT_IDENTIFIER,
  ARCHIVE_IDENTIFIER,
  CHANGE_FIELD_IDENTIFIER,
  SEND_SURVEY_IDENTIFIER,
  MODIFY_AUTOMATION_IDENTIFIER,
  START_AGENTIC_WORKFLOW_IDENTIFIER,
  moneyFormatter,
  numberFormatter,
  CUSTOM_ACTION_IDENTIFIER,
  SECONDARY_ACTIONS,
  SMART_CONNECTOR_IDENTIFIER,
  doesActionGenerateFile,
} from './model';
import {
  BulkActionInfoWrapper,
  BulkActionTextWrapper,
} from './StepsContent/styles';
import { StyledDescription } from './styles';

type AutomationSummaryStepProps = {
  automationName?: string;
  summary?: Summary['stats'] & { text: string };
  description?: string;
  isPause: boolean;
  isStartAndResume: boolean;
  isCancel: boolean;
};

type EmailData = {
  baseMessageId: string;
  craftJsContent: Record<string, any>;
  htmlContent: string;
  kznFilters: Record<string, any>;
  name: string;
  sender: {
    customName: string;
    type:
      | 'specific_team_member'
      | 'team_member_field'
      | 'last_team_member_role'
      | 'last_team_member'
      | 'business'
      | 'entity_record_owner'
      | 'specific_integrated_inbox';
    id: string;
  };
  senderTeamMember: {
    displayName: string;
    email: string;
    firstName: string;
    id: string;
    lastName: string;
  };
  subject: string;
};

type EmailStepData = {
  clearEmail(): void;
  email: EmailData;
  emailStatus:
    | 'opted_in'
    | 'not_opted_in'
    | 'unsubscribed'
    | 'suppression_list';
  onChangeEmail(email: EmailData): void;
  onChangeEmailStatus(option: { label: string; value: string }): void;
  onChangeSubscriptionList(options: { label: string; value: string }[]): void;
  subscriptionListOptions: UseGetSummaryOptions['subscriptionListOptions'];
  subscriptionLists: UseGetSummaryOptions['subscriptionLists'];
};

interface SummaryStepsProps
  extends Pick<
    UseGetSummaryOptions,
    'automation' | 'customAction' | 'secondaryAction'
  > {
  fieldData?: {
    field?: {
      displayName: string;
      id: string;
      name: string;
      fieldType: FieldType;
      options: Array<{ id: string; name: string }>;
      moneyOptions?: { id: string; currency: string; symbol: string };
    };
    fieldValue?: any;
    removeTags: Array<{ id: string; label: string }>;
    resolution: {
      filterable: boolean;
      label: string;
      value: 'overwrite' | 'update_if_blank';
    };
  };
  isLoadingSummary: boolean;
  objectInfo: {
    defaultTitleData?: { affix: string; text: string };
    entityName?: string;
    isContacts?: boolean;
    names: { singular: string; plural: string };
  };
  resumePausedAutomations?: boolean;
  sendEmailStep?: EmailStepData;
  sendTextMergeFields?: MergeFields;
  sendTextValue?: string;
  sendTextName?: string;
  subscriptionItem?: { label: string; value: string };
  subscriptionStatus?: 'opted_in' | 'not_opted_in' | 'opted_out';
  summary?: Summary['stats'] & { text: string };
  surveyValue?: { label: string; value: string };
  tagsToAdd?: Array<{ id: string; label: string }>;
  tagsToRemove?: Array<{ id: string; label: string }>;
  teamMembers?: Array<{
    data: {
      displayName: string;
      email: string;
      firstName: string;
      id: string;
      lastName: string;
    };
    label: string;
    value: string;
  }>;
  userAction: UseGetSummaryOptions['selectedAction'];
  sendEmailNotification?: boolean;
  setSendEmailNotification?: Dispatch<SetStateAction<boolean>>;
  smartConnector?: { label: string; value: string };
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;

  .text-templater {
    > span {
      font-weight: ${fontWeights.bold};
    }
    margin-bottom: 20px;
    & > div {
      height: 148px;
    }
  }
`;

const NotificationSettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 13px;
`;

const StyledSummaryValue = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const SummaryValueContainer = ({ children }: PropsWithChildren) => {
  return (
    <StyledSummaryValue data-qa="summary-value">{children}</StyledSummaryValue>
  );
};

const Loader = styled(KizenLoader)`
  width: 100%;
  text-align: center;
  padding: 10px !important;
`;

export const AutomationSummaryStep = (props: AutomationSummaryStepProps) => {
  const {
    automationName,
    summary,
    description,
    isPause,
    isStartAndResume,
    isCancel,
  } = props;

  const { t } = useTranslation();

  const { automations = null } = summary ?? {};

  const showAutomationSummary =
    Boolean(automations) && (isPause || isStartAndResume || isCancel);

  return (
    <SummaryValueContainer>
      <BulkActionTextWrapper>
        <KizenTypography weight="bold" as="span">
          {t('Agentic Workflow')}:&nbsp;
        </KizenTypography>
        <KizenTypography as="span">{automationName}</KizenTypography>
      </BulkActionTextWrapper>
      {showAutomationSummary ? (
        <BulkActionTextWrapper data-qa="automation-summary">
          {isPause ? (
            <>
              <KizenTypography weight="bold" as="span">
                {t('{{count}} active agentic workflows', {
                  count: automations?.active_count,
                })}
              </KizenTypography>{' '}
              <KizenTypography as="span">{t('will be paused')}</KizenTypography>
            </>
          ) : null}
          {isStartAndResume ? (
            <>
              <KizenTypography weight="bold" as="span">
                {t('{{count}} paused agentic workflows', {
                  count: automations?.paused_count,
                })}
              </KizenTypography>{' '}
              <KizenTypography as="span">
                {t('will be resumed')}
              </KizenTypography>
            </>
          ) : null}
          {isCancel ? (
            <>
              <div>
                <KizenTypography weight="bold" as="span">
                  {t('{{count}} active agentic workflows', {
                    count: automations?.active_count,
                  })}
                </KizenTypography>{' '}
                <KizenTypography as="span">
                  {t('will be canceled')}
                </KizenTypography>
              </div>
              <div>
                <KizenTypography weight="bold" as="span">
                  {t('{{count}} paused agentic workflows', {
                    count: automations?.paused_count,
                  })}
                </KizenTypography>{' '}
                <KizenTypography as="span">
                  {t('will be canceled')}
                </KizenTypography>
              </div>
            </>
          ) : null}
        </BulkActionTextWrapper>
      ) : null}
      {description ? (
        <StyledDescription>
          <KizenTypography>{description}</KizenTypography>
        </StyledDescription>
      ) : null}
    </SummaryValueContainer>
  );
};

const Steps = (
  props: Omit<
    SummaryStepsProps,
    | 'isLoadingSummary'
    | 'sendTextMergeFields'
    | 'sendEmailNotification'
    | 'setSendEmailNotification'
  >
) => {
  const {
    userAction,
    sendTextValue,
    sendTextName,
    tagsToAdd = [],
    tagsToRemove = [],
    teamMembers = [],
    subscriptionItem,
    subscriptionStatus,
    fieldData,
    sendEmailStep,
    surveyValue,
    automation,
    objectInfo,
    summary,
    secondaryAction,
    resumePausedAutomations,
    customAction,
    smartConnector,
  } = props;

  const { email } = sendEmailStep ?? {};
  const { fieldValue, field, removeTags, resolution } = fieldData ?? {};

  const { t } = useTranslation();

  if (userAction) {
    switch (userAction.value) {
      case SEND_TEXT_IDENTIFIER:
        return (
          <SummaryValueContainer>
            <BulkActionTextWrapper>
              <KizenTypography weight="bold" as="span">
                {t('Broadcast Name')}:&nbsp;
              </KizenTypography>
              <KizenTypography as="span">{sendTextName}</KizenTypography>
            </BulkActionTextWrapper>
            <BulkActionTextWrapper>
              <KizenTypography weight="bold" as="span">
                {t('Text Message Body')}:&nbsp;
              </KizenTypography>
              <TextTemplater
                disabled
                defaultValue={sendTextValue}
                customize={{
                  disableFocus: true,
                  showCountComponent: false,
                  showLabel: false,
                  showSelectComponent: false,
                }}
                textareaOptions={{
                  enableToolbar: false,
                  initialHeight: 140,
                }}
              />
            </BulkActionTextWrapper>
          </SummaryValueContainer>
        );
      case SEND_EMAIL_IDENTIFIER:
        return (
          <SummaryValueContainer>
            <BulkActionTextWrapper>
              <KizenTypography weight="bold" as="span">
                {t('Email')}:&nbsp;
              </KizenTypography>
              <KizenTypography as="span">{email?.name}</KizenTypography>
            </BulkActionTextWrapper>
          </SummaryValueContainer>
        );
      case CHANGE_TAGS_IDENTIFIER:
        return (
          <SummaryValueContainer>
            {!!tagsToAdd.length && (
              <BulkActionTextWrapper>
                <KizenTypography weight="bold" as="span">
                  {t('Tags to Add')}:&nbsp;
                </KizenTypography>
                <KizenTypography as="span">
                  {tagsToAdd.map((tag) => tag.label).join(', ')}
                </KizenTypography>
              </BulkActionTextWrapper>
            )}
            {!!tagsToRemove.length && (
              <BulkActionTextWrapper>
                <KizenTypography weight="bold" as="span">
                  {t('Tags to Remove')}:&nbsp;
                </KizenTypography>
                <KizenTypography as="span">
                  {tagsToRemove.map((tag) => tag.label).join(', ')}
                </KizenTypography>
              </BulkActionTextWrapper>
            )}
          </SummaryValueContainer>
        );
      case MANAGE_TEAM_IDENTIFIER:
        return (
          <SummaryValueContainer>
            <BulkActionTextWrapper>
              <KizenTypography weight="bold" as="span">
                {t('Team {{entity}} to Associate', {
                  entity: teamMembers.length === 1 ? t('Member') : t('Members'),
                })}
                :&nbsp;
              </KizenTypography>
              <KizenTypography as="span">
                {teamMembers.map((tag) => tag.label).join(', ')}
              </KizenTypography>
            </BulkActionTextWrapper>
          </SummaryValueContainer>
        );
      case MANAGE_SUBSCRIPTIONS: {
        const label = subscriptionStatus
          ? `${t('Subscription Lists to Mark as')} ${subscriptionStatusLabel[subscriptionStatus](t)}: `
          : '';

        return (
          <SummaryValueContainer>
            <BulkActionTextWrapper>
              <KizenTypography weight="bold" as="span">
                {`${label} ${subscriptionItem?.label}`}
              </KizenTypography>
            </BulkActionTextWrapper>
          </SummaryValueContainer>
        );
      }
      case EXPORT_IDENTIFIER:
        return (
          <SummaryValueContainer>
            <BulkActionTextWrapper>
              <KizenTypography weight="bold" as="span">
                {objectInfo.names.singular} {t('Export')}
              </KizenTypography>
            </BulkActionTextWrapper>
          </SummaryValueContainer>
        );
      case ARCHIVE_IDENTIFIER:
        return (
          <SummaryValueContainer>
            <BulkActionTextWrapper>
              <KizenTypography weight="bold" as="span">
                {objectInfo.names.singular} {t('Archive')}
              </KizenTypography>
            </BulkActionTextWrapper>
          </SummaryValueContainer>
        );
      case CHANGE_FIELD_IDENTIFIER: {
        const getFieldValue = () => {
          switch (field?.fieldType) {
            case FIELD_TYPES.Date.type:
              if (!fieldValue) {
                return fieldValue;
              }
              return format(parseDateFromApi(fieldValue), 'MM/dd/yyyy');
            case FIELD_TYPES.DateTime.type:
              if (!fieldValue) {
                return fieldValue;
              }
              return format(fieldValue, 'MM/dd/yyyy h:mm aaa');
            case FIELD_TYPES.TeamSelector.type: {
              const key = 'displayName';
              if (Array.isArray(fieldValue)) {
                return fieldValue.map((item) => item[key]).join(', ');
              }
              return fieldValue?.[key] ?? '';
            }
            case FIELD_TYPES.Dropdown.type:
            case FIELD_TYPES.Radio.type:
            case FIELD_TYPES.Status.type:
              return fieldValue.name || '';
            case 'yesnomaybe':
            case FIELD_TYPES.YesNoMaybe.type: {
              const option = field.options.find(({ id }) => id === fieldValue);
              return option ? option.name : '';
            }
            case FIELD_TYPES.Checkbox.type:
              return String(!!fieldValue);
            case FIELD_TYPES.Relationship.type: {
              if (Array.isArray(fieldValue)) {
                return fieldValue.map(({ label }) => label).join(', ');
              }
              return fieldValue.label ?? '';
            }
            case FIELD_TYPES.Checkboxes.type:
            case FIELD_TYPES.Files.type:
              return fieldValue
                ? fieldValue.map(({ name }: any) => name).join(', ')
                : '';
            case FIELD_TYPES.Money.type: {
              const result = isNumber(fieldValue)
                ? moneyFormatter(fieldValue)
                : '';
              return (
                `${result ? field.moneyOptions?.symbol || '' : ''}${result}` ||
                ''
              );
            }
            case FIELD_TYPES.Decimal.type: // save as it has no moneyoptions to add
            case FIELD_TYPES.Integer.type:
              return (
                `${field.moneyOptions?.symbol || ''}${
                  typeof fieldValue === 'number'
                    ? numberFormatter(fieldValue)
                    : ''
                }` || ''
              );
            default:
              return isNumber(fieldValue)
                ? fieldValue.toString()
                : fieldValue || '';
          }
        };
        return (
          <SummaryValueContainer>
            {field?.fieldType === FIELD_TYPES.DynamicTags.type ? (
              <>
                {!!fieldValue && fieldValue.length > 0 && (
                  <BulkActionTextWrapper>
                    <KizenTypography weight="bold" as="span">
                      {t('"{{displayName}}" Options to Add', field)}:{' '}
                    </KizenTypography>
                    <KizenTypography as="span">
                      {fieldValue.map(({ label }: any) => label).join(', ')}
                    </KizenTypography>
                  </BulkActionTextWrapper>
                )}
                {!!removeTags && removeTags.length > 0 && (
                  <BulkActionTextWrapper>
                    <KizenTypography weight="bold" as="span">
                      {t('"{{displayName}}" Options to Remove', field)}:{' '}
                    </KizenTypography>
                    <KizenTypography as="span">
                      {removeTags.map(({ label }) => label).join(', ')}
                    </KizenTypography>
                  </BulkActionTextWrapper>
                )}
              </>
            ) : (
              <BulkActionTextWrapper>
                <KizenTypography weight="bold" as="span">
                  {t('"{{displayName}}" Value', field)}:
                </KizenTypography>
                <KizenTypography as="span">
                  <span className="field-value">{` ${getFieldValue()}`}</span>
                </KizenTypography>
              </BulkActionTextWrapper>
            )}
            <BulkActionTextWrapper>
              <KizenTypography weight="bold" as="span">
                {t('Field Resolution')}:{' '}
              </KizenTypography>
              <KizenTypography as="span">{resolution?.label}</KizenTypography>
            </BulkActionTextWrapper>
          </SummaryValueContainer>
        );
      }
      case SEND_SURVEY_IDENTIFIER: {
        return (
          <SummaryValueContainer>
            <BulkActionTextWrapper>
              <KizenTypography weight="bold" as="span">
                {t('Survey to Send')}:&nbsp;
              </KizenTypography>
              <KizenTypography as="span">{surveyValue?.label}</KizenTypography>
            </BulkActionTextWrapper>
          </SummaryValueContainer>
        );
      }
      case MODIFY_AUTOMATION_IDENTIFIER: {
        return (
          <AutomationSummaryStep
            summary={summary}
            automationName={automation?.label!}
            isPause={secondaryAction === SECONDARY_ACTIONS.PAUSE}
            isStartAndResume={
              secondaryAction === SECONDARY_ACTIONS.START &&
              Boolean(resumePausedAutomations)
            }
            isCancel={secondaryAction === SECONDARY_ACTIONS.CANCEL}
          />
        );
      }
      case START_AGENTIC_WORKFLOW_IDENTIFIER: {
        return (
          <AutomationSummaryStep
            summary={summary}
            automationName={automation?.label!}
            isPause={false}
            isStartAndResume={Boolean(resumePausedAutomations)}
            isCancel={false}
          />
        );
      }
      case CUSTOM_ACTION_IDENTIFIER: {
        const { automation, smartConnector, action, description } =
          customAction || {};

        return smartConnector ? (
          <SummaryValueContainer>
            <BulkActionTextWrapper>
              <KizenTypography weight="bold" as="span">
                {t('SmartConnector to Start')}:&nbsp;
              </KizenTypography>
              <KizenTypography as="span">{smartConnector.name}</KizenTypography>
            </BulkActionTextWrapper>
          </SummaryValueContainer>
        ) : (
          <AutomationSummaryStep
            summary={summary}
            automationName={automation?.name}
            description={description}
            isPause={action === 'pause_automation'}
            isStartAndResume={action === 'start_and_resume_automation'}
            isCancel={action === 'cancel_automation'}
          />
        );
      }
      case SMART_CONNECTOR_IDENTIFIER:
        return (
          <SummaryValueContainer>
            <BulkActionTextWrapper>
              <KizenTypography weight="bold" as="span">
                {t('SmartConnector to Start')}:&nbsp;
              </KizenTypography>
              <KizenTypography as="span">
                {smartConnector?.label}
              </KizenTypography>
            </BulkActionTextWrapper>
          </SummaryValueContainer>
        );
      default: {
        return (
          <SummaryValueContainer>{t('Unknown step')}</SummaryValueContainer>
        );
      }
    }
  }
  return null;
};

const SummaryStep = (props: SummaryStepsProps) => {
  const {
    sendTextValue,
    sendTextName,
    summary,
    userAction,
    tagsToAdd,
    tagsToRemove,
    teamMembers,
    subscriptionItem,
    subscriptionStatus,
    fieldData,
    sendEmailStep,
    surveyValue,
    automation,
    objectInfo,
    secondaryAction,
    isLoadingSummary,
    resumePausedAutomations,
    customAction,
    smartConnector,
    sendEmailNotification,
    setSendEmailNotification,
  } = props;

  const { subscriptionLists, subscriptionListOptions } = sendEmailStep ?? {};

  const { t } = useTranslation();

  const { count, countNum, selected, notIncludedString } =
    useSummaryCounts(summary);

  const showNotificationOptions =
    sendEmailNotification !== undefined &&
    setSendEmailNotification !== undefined;

  return isLoadingSummary || !userAction ? (
    <Loader loading />
  ) : (
    <Wrapper>
      <BulkActionInfoWrapper>
        <BulkActionTextWrapper>
          <KizenTypography as="span">
            {t('This action will be performed on')}&nbsp;
          </KizenTypography>
          <KizenTypography weight="bold" as="span" data-qa="total">
            {count}{' '}
            {countNum === 1
              ? objectInfo.names.singular
              : objectInfo.names.plural}
          </KizenTypography>
        </BulkActionTextWrapper>
        {selected ? (
          <BulkActionTextWrapper>
            <KizenTypography weight="bold" as="span">
              {t('Selected')} {objectInfo.names.plural}:&nbsp;
            </KizenTypography>
            <KizenTypography as="span" data-qa="selected">
              {selected}
            </KizenTypography>
          </BulkActionTextWrapper>
        ) : null}
        {userAction.value === SEND_EMAIL_IDENTIFIER && (
          <BulkActionTextWrapper>
            <KizenTypography weight="bold" as="span">
              {t('Subscription Lists')}:&nbsp;
            </KizenTypography>
            <KizenTypography as="span">
              {!subscriptionLists && t('Any Opted In')}
              {subscriptionLists &&
                subscriptionListOptions
                  ?.filter((opt) => {
                    if (!subscriptionLists?.length) {
                      // When this list is empty we want to include all subscription lists
                      return true;
                    }

                    return subscriptionLists.includes(opt.value);
                  })
                  .map((opt) => opt.label)
                  .join(', ')}
            </KizenTypography>
          </BulkActionTextWrapper>
        )}
        {notIncludedString ? (
          <BulkActionTextWrapper>
            <KizenTypography weight="bold" as="span">
              {t('Not Included')}:&nbsp;
            </KizenTypography>
            <KizenTypography as="span" data-qa="not-included">
              {notIncludedString}
            </KizenTypography>
          </BulkActionTextWrapper>
        ) : null}
      </BulkActionInfoWrapper>
      <Steps
        summary={summary}
        sendTextValue={sendTextValue}
        sendTextName={sendTextName}
        userAction={userAction}
        tagsToAdd={tagsToAdd}
        tagsToRemove={tagsToRemove}
        teamMembers={teamMembers}
        subscriptionItem={subscriptionItem}
        subscriptionStatus={subscriptionStatus}
        fieldData={fieldData}
        sendEmailStep={sendEmailStep}
        surveyValue={surveyValue}
        automation={automation}
        objectInfo={objectInfo}
        secondaryAction={secondaryAction}
        resumePausedAutomations={resumePausedAutomations}
        customAction={customAction}
        smartConnector={smartConnector}
      />
      {showNotificationOptions && (
        <NotificationSettingsContainer>
          <KizenTypography weight="bold">
            {t('Notification Options')}
          </KizenTypography>
          <KizenTypography as="span">
            {doesActionGenerateFile(userAction.value)
              ? t(
                  'Once the action completes, your file will be ready for download in the Notification Center.'
                )
              : t(
                  'Once the action completes, a notification will appear in the Notification Center.'
                )}
          </KizenTypography>
          <Switch
            removeMargin
            checked={sendEmailNotification}
            label={t('Also email me when complete')}
            onChange={() => setSendEmailNotification((x) => !x)}
          />
        </NotificationSettingsContainer>
      )}
    </Wrapper>
  );
};

export default SummaryStep;
